import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, CameraViewer, EmptyState, ExplainerBanner, FlowLayout, Sheet, Skeleton } from '@/ds'
import { useAppNavigate } from '@/lib/query-params'
import { CaptureError } from '@/services/checkin/capture'
import type { CaptureErrorCode } from '@/services/checkin/capture'
import { SdkSlot, useCheckinFlow } from '../CheckinFlow'
import { backLabelKey, neighbours, progressFor, stepPath } from '../flow'
import { CaptureErrorState } from '../captureErrors'

type Status = { kind: 'ready' } | { kind: 'capturing' } | { kind: 'validating' } | { kind: 'mismatch' } | { kind: 'error'; code: CaptureErrorCode }
const AUTO = 'v2.legacy.stepper.autoCollect'

/**
 * Paso 4 · Una foto de tu cara (CheckIn.dc.html step=4). Solo si la propiedad la exige (forceSelfie).
 * Mismo SDK y mismas llamadas que hoy (hideDocumentsAndShowFace, validate FACE); la cara se captura
 * después de revisar los datos (D32). Si no coincide y la propiedad lo permite, se puede continuar.
 */
export function Cara() {
  const { t } = useTranslation()
  const { go } = useAppNavigate()
  const { stay, draft, capture, setVolatile, volatile } = useCheckinFlow()
  const [status, setStatus] = useState<Status>({ kind: 'ready' })
  const [why, setWhy] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const { prev, next } = neighbours('4', stay, draft.persona, draft.method, draft.entry)
  const allowSkip = stay.registration.rules.allowFaceMatchSkip

  useEffect(() => {
    capture.startFace((e) => {
      if (e.type === 'capturing') setStatus({ kind: 'capturing' })
      else if (e.type === 'error') setStatus({ kind: 'error', code: e.code })
      else if (e.type === 'captured') {
        setStatus({ kind: 'validating' })
        capture
          .validateFace()
          .then(({ valid, face }) => {
            if (!valid) return setStatus({ kind: 'mismatch' })
            setVolatile({ attachments: { ...volatile.attachments, face } })
            go(stepPath(next))
          })
          .catch((err: unknown) => setStatus({ kind: 'error', code: err instanceof CaptureError ? err.code : 'connection' }))
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt])

  const repeat = () => {
    setStatus({ kind: 'ready' })
    setAttempt((a) => a + 1)
  }

  return (
    <FlowLayout
      progress={{ ...progressFor('4', stay, draft.persona, draft.method, draft.entry), backLabel: t(backLabelKey(prev)), onBack: () => go(stepPath(prev)) }}
      title={t('v2.checkin.cara.titulo')}
      intro={t('v2.checkin.cara.intro', { property: stay.property.name })}
      primary={status.kind === 'ready' || status.kind === 'capturing' ? { label: t('v2.checkin.documento.hacerFoto'), icon: 'camera', loading: status.kind === 'capturing', onClick: () => capture.captureFace() } : undefined}
      secondary={status.kind === 'ready' ? { label: t('v2.checkin.cara.porque'), onClick: () => setWhy(true) } : undefined}
    >
      {status.kind === 'error' ? (
        <CaptureErrorState code={status.code} qr={false} onRetry={repeat} onManual={null} />
      ) : status.kind === 'mismatch' ? (
        <div className="checkin__stack">
          <EmptyState tone="error" icon="alert-triangle" title={t(`${AUTO}.errors.faceMatchFailed.title`)} actionLabel={t('v2.checkin.revision.repetir')} onAction={repeat}>
            {allowSkip ? t(`${AUTO}.errors.faceMatchFailed.description`) : t(`${AUTO}.errors.faceMatchFailed.descriptionRequired`)}
          </EmptyState>
          {allowSkip && (
            <Button variant="secondary" onClick={() => go(stepPath(next))}>
              {t('v2.checkin.continuar')}
            </Button>
          )}
        </div>
      ) : status.kind === 'validating' ? (
        <div className="checkin__processing" role="status" aria-busy="true">
          <Skeleton height={220} radius={16} />
          <p className="checkin__processing-title">{t(`${AUTO}.verifyingIdentity.title`)}</p>
          <p className="checkin__note">{t(`${AUTO}.verifyingIdentity.description`)}</p>
        </div>
      ) : (
        <CameraViewer guide="face" hint={t('v2.checkin.cara.consejo')}>
          <SdkSlot mode="face" />
        </CameraViewer>
      )}
      <ExplainerBanner title={t('v2.checkin.cara.bannerTitulo')}>{t('v2.checkin.cara.bannerTexto')}</ExplainerBanner>
      <Sheet open={why} title={t('v2.checkin.cara.porque')} confirmHidden onCancel={() => setWhy(false)}>
        <p>{t('v2.checkin.cara.intro', { property: stay.property.name })}</p>
        <p>{t('v2.checkin.cara.bannerTexto')}</p>
      </Sheet>
    </FlowLayout>
  )
}
